import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Chip,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CircularProgress } from '@mui/material';

const statusColors = {
  'Bekliyor': 'warning',
  'Onaylandı': 'info',
  'Hazırlanıyor': 'primary',
  'Kargoya Verildi': 'secondary',
  'Tamamlandı': 'success',
  'İptal Edildi': 'default'
};

const notificationSound = new Audio('/notification.mp3');

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openOrder, setOpenOrder] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    
    const q = query(
      collection(db, 'orders'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (orders.length > 0 && ordersData.length > orders.length) {
        notificationSound.play().catch(err => console.log('Ses çalınamadı:', err));
      }

      setOrders(ordersData);
      setLoading(false);
    }, (error) => {
      console.error("Sipariş verilerini alma hatası:", error);
      setError('Siparişler yüklenirken bir hata oluştu');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        adminCancelled: newStatus === 'İptal Edildi' ? true : false
      });

      const order = orders.find(o => o.id === orderId);
      if (order?.userId) {
        const notificationData = {
          userId: order.userId,
          title: 'Sipariş Durumu Güncellendi',
          message: `#${orderId.slice(0, 8)} numaralı siparişinizin durumu "${newStatus}" olarak güncellendi.`,
          type: 'order_status',
          orderId: orderId,
          createdAt: serverTimestamp(),
          read: false
        };

        await addDoc(collection(db, 'notifications'), notificationData);
      }

      setSuccess('Sipariş durumu güncellendi');
    } catch (error) {
      console.error('Sipariş güncellenirken hata:', error);
      setError('Sipariş güncellenirken bir hata oluştu');
    }
  };

  const filteredOrders = orders.filter(order => 
    order.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Card elevation={0} sx={{ bgcolor: '#f8f8f8', borderRadius: 2 }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Sipariş Yönetimi
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Sipariş ID'sine göre ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3, maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Grid container spacing={3}>
              {filteredOrders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card sx={{ 
                    bgcolor: order.status === 'İptal Edildi' && !order.adminCancelled ? '#f5f5f5' : 'white',
                    borderLeft: order.status === 'İptal Edildi' && !order.adminCancelled ? '4px solid black' : 'none'
                  }}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2 
                      }}>
                        <Typography variant="h6">
                          Sipariş #{order.id.slice(0, 8)}
                        </Typography>
                        <Box>
                          <Chip
                            label={order.status}
                            color={statusColors[order.status]}
                            sx={{ mr: 1 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenOrder(openOrder === order.id ? null : order.id)}
                          >
                            {openOrder === order.id ? 'Gizle' : 'Detaylar'}
                          </Button>
                        </Box>
                      </Box>

                      <Collapse in={openOrder === order.id}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                  Müşteri Bilgileri
                                </Typography>
                                <Typography variant="body2">
                                  Email: {order.userEmail}
                                </Typography>
                                <Typography variant="body2">
                                  Ad Soyad: {order.shippingAddress?.fullName}
                                </Typography>
                                <Typography variant="body2">
                                  Telefon: {order.shippingAddress?.phone}
                                </Typography>
                                <Typography variant="body2">
                                  Adres: {order.shippingAddress?.address}
                                </Typography>
                                <Typography variant="body2">
                                  Şehir: {order.shippingAddress?.city}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                  Sipariş Durumu
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                  {Object.keys(statusColors).map((status) => (
                                    <Chip
                                      key={status}
                                      label={status}
                                      color={statusColors[status]}
                                      onClick={() => handleUpdateStatus(order.id, status)}
                                      sx={{ m: 0.5 }}
                                      variant={order.status === status ? 'filled' : 'outlined'}
                                    />
                                  ))}
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary">
                                  Sipariş Tarihi: {order.createdAt?.toDate().toLocaleString('tr-TR')}
                                </Typography>
                                {order.updatedAt && (
                                  <Typography variant="body2" color="text.secondary">
                                    Son Güncelleme: {order.updatedAt.toDate().toLocaleString('tr-TR')}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                  Sipariş Detayı
                                </Typography>
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Ürün</TableCell>
                                        <TableCell>Beden</TableCell>
                                        <TableCell>Renk</TableCell>
                                        <TableCell align="right">Adet</TableCell>
                                        <TableCell align="right">Birim Fiyat</TableCell>
                                        <TableCell align="right">Toplam</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {order.items?.map((item, index) => (
                                        <TableRow key={index}>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              {item.image && (
                                                <Box
                                                  component="img"
                                                  src={item.image}
                                                  alt={item.title}
                                                  sx={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: 'cover',
                                                    borderRadius: 1
                                                  }}
                                                />
                                              )}
                                              <Typography variant="body2">{item.title}</Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell>{item.size || '-'}</TableCell>
                                          <TableCell>
                                            {item.color ? (
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    bgcolor: item.color.toLowerCase(),
                                                    border: '1px solid #ddd'
                                                  }}
                                                />
                                                {item.color}
                                              </Box>
                                            ) : (
                                              '-'
                                            )}
                                          </TableCell>
                                          <TableCell align="right">{item.quantity}</TableCell>
                                          <TableCell align="right">
                                            {item.price?.toLocaleString('tr-TR', { 
                                              style: 'currency', 
                                              currency: 'TRY' 
                                            })}
                                          </TableCell>
                                          <TableCell align="right">
                                            {(item.price * item.quantity)?.toLocaleString('tr-TR', { 
                                              style: 'currency', 
                                              currency: 'TRY' 
                                            })}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow>
                                        <TableCell colSpan={4} />
                                        <TableCell align="right">
                                          <strong>Toplam</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>
                                            {order.totalAmount?.toLocaleString('tr-TR', { 
                                              style: 'currency', 
                                              currency: 'TRY' 
                                            })}
                                          </strong>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      </Box>
    </AdminLayout>
  );
}

export default AdminOrders;